'use client';

import { Suspense, useEffect, useState } from 'react';
import LanguageChanger from '@/i18n/components/language-changer';
import Logo from './logo';
import clsx from 'clsx';
import MenuDropdownButton from './top-menu-dropdown-button';
import CloseMenuIconButton from '@/components/ui/common/close-button';
import useContactUsContext from '@/components/providers/context/contact-us-context';
import { useRouter } from 'next/navigation';
import LoadingPlaceholder from '@/components/ui/common/loading-placeholder';
import { useLocale } from '@/components/providers/context/locale-context';
import { getLocalizedHref } from '@/app/lib/utils/localized-href';

// ─── 汉堡菜单图标 ─────────────────────────────────────────────────────────────

function MenuIcon({
    fill,
    onClick,
}: Readonly<{
    fill: 'black' | 'white';
    onClick: () => void;
}>) {
    return (
        <button
            onClick={onClick}
            style={{ WebkitTapHighlightColor: 'transparent' }}
        >
            <svg
                viewBox="0 0 1024 1024"
                xmlns="http://www.w3.org/2000/svg"
                width="28"
                height="28"
                fill={fill}
            >
                <path d="M133.973333 232.106667h756.48v85.333333H133.973333zM133.973333 469.333333h756.48v85.333334H133.973333zM133.973333 706.56h756.48v85.333333H133.973333z" />
            </svg>
        </button>
    );
}

// ─── 子菜单展开列表 ────────────────────────────────────────────────────────────

function SubMenuList({
    label,
    menuText,
    title,
    subMenus,
    isOpen,
    onToggle,
    onNavigate,
    subMenuTitleColor,
    subMenuItemColor,
    activeBg,
}: Readonly<{
    label: string;
    menuText: string;
    title: string;
    subMenus: { title: string; href: string; index: number }[];
    isOpen: boolean;
    onToggle: () => void;
    onNavigate: (href: string) => void;
    subMenuTitleColor: string;
    subMenuItemColor: string;
    activeBg: string;
}>) {
    return (
        <div className="j-h5 flex w-full flex-col items-center">
            <button
                aria-label={label}
                aria-expanded={isOpen}
                className={clsx('h-[60px] w-full', activeBg)}
                onClick={onToggle}
            >
                <MenuDropdownButton
                    isFullWidth
                    title={menuText}
                    dropdownOpen={isOpen}
                />
            </button>

            {isOpen && (
                <div className="flex w-full flex-col">
                    <div className={clsx('j-p1', subMenuTitleColor)}>
                        {title}
                    </div>
                    <div
                        className={clsx(
                            'j-t1 flex w-full flex-col',
                            subMenuItemColor,
                        )}
                    >
                        {subMenus.map((menu) => (
                            <button
                                key={menu.title
                                    .toLowerCase()
                                    .replace(/ /g, '-')}
                                className="flex h-[40px] w-full items-center"
                                onClick={() => onNavigate(menu.href)}
                            >
                                <div>{menu.title}</div>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── 主组件 ───────────────────────────────────────────────────────────────────

export default function TopMenu4Mobile({
    homeMenuText,
    productMenuText,
    companyMenuText,
    contactUsMenuText,
    textColor,
    bgColor,
    productTitle,
    companyTitle,
    productSubMenus,
    companySubMenus,
    blogMenuText,
    blogTitle,
    blogSubMenus,
}: Readonly<{
    homeMenuText: string;
    productMenuText: string;
    companyMenuText: string;
    blogMenuText: string;
    blogTitle: string;
    blogSubMenus: { title: string; href: string; index: number }[];
    contactUsMenuText: string;
    textColor: 'black' | 'white';
    bgColor: string;
    productTitle: string;
    companyTitle: string;
    productSubMenus: { title: string; href: string; index: number }[];
    companySubMenus: { title: string; href: string; index: number }[];
}>) {
    const { openForm } = useContactUsContext();
    const locale = useLocale();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isMenuHidden, setIsMenuHidden] = useState(true);
    const [openSubMenu, setOpenSubMenu] = useState<
        'product' | 'company' | 'blog' | null
    >(null);
    const router = useRouter();

    // 菜单打开/关闭动画：关闭时延迟隐藏，等待 slideOut 动画完成
    useEffect(() => {
        const delay = isMenuOpen ? 0 : 500;
        const timer = setTimeout(() => setIsMenuHidden(!isMenuOpen), delay);
        return () => clearTimeout(timer);
    }, [isMenuOpen]);

    const closeMenu = () => setIsMenuOpen(false);

    const navigate = (href: string) => {
        const localizedHref = getLocalizedHref(href, locale);
        closeMenu();
        router.prefetch(localizedHref);
        router.push(localizedHref);
    };

    const toggleSubMenu = (key: 'product' | 'company' | 'blog') =>
        setOpenSubMenu((prev) => (prev === key ? null : key));

    // ─── 颜色主题（集中管理抽屉菜单所有颜色）────────────────────────────────────
    const theme = {
        /** 抽屉背景 */
        drawerBg: 'bg-white',
        /** 关闭按钮颜色 */
        closeIconFill: '#000000',
        /** 菜单列表文字颜色 */
        listText: 'text-black',
        /** 子菜单标题（如 BLOG、PRODUCT）颜色 */
        subMenuTitleColor: 'text-[#878789]',
        /** 子菜单选项文字颜色 */
        subMenuItemColor: 'text-black',
        /** 菜单项点击高亮背景 */
        menuItemActiveBg: 'active:bg-[#E9E9E9]',
        /** Contact Us 按钮边框颜色 */
        contactBorder: 'border-black',
        /** Contact Us 按钮背景 */
        contactBg: 'bg-white',
        /** Contact Us 按钮文字颜色 */
        contactText: 'text-black',
    } as const;

    return (
        <div
            className={clsx('relative h-full w-full', {
                'text-black': textColor === 'black',
                'text-white': textColor === 'white',
                'bg-white': bgColor === 'white',
                'bg-transparent': bgColor === 'transparent',
            })}
        >
            {/* 顶栏：Logo + 汉堡按钮 */}
            <div className="flex h-full w-full items-center justify-between px-[20px]">
                <Logo color={textColor} height={26} width={94} />
                <div className="flex flex-row items-center gap-[16px]">
                    <button
                        onClick={openForm}
                        className={clsx(
                            'j-t2 flex h-[28px] items-center justify-center rounded-[47px] border px-[14px]',
                            theme.contactBorder,
                            theme.contactBg,
                            theme.contactText,
                        )}
                    >
                        {contactUsMenuText}
                    </button>
                    <MenuIcon
                        fill={textColor}
                        onClick={() => setIsMenuOpen(true)}
                    />
                </div>
            </div>

            {/* 全屏抽屉菜单 */}
            <div
                className={clsx(
                    'h-screen-safe absolute left-0 top-0 flex w-full flex-col transition-transform',
                    theme.drawerBg,
                    isMenuOpen ? 'animate-slideIn' : 'animate-slideOut',
                    { hidden: isMenuHidden, block: !isMenuHidden },
                )}
                style={{ WebkitTapHighlightColor: 'transparent' }}
            >
                {/* 关闭按钮 */}
                <div className="flex w-full justify-end pr-[16px] pt-[16px]">
                    <CloseMenuIconButton
                        onClose={closeMenu}
                        width={28}
                        height={28}
                        fill={theme.closeIconFill}
                    />
                </div>

                {/* 菜单项列表 */}
                <div className={clsx('mx-[24px] mt-[36px]', theme.listText)}>
                    <div className="flex flex-col items-start divide-y">
                        {/* Home */}
                        <button
                            className="flex h-[60px] w-full items-center justify-start"
                            onClick={() => navigate('/')}
                        >
                            <div className="j-h5 flex h-full w-full items-center">
                                {homeMenuText}
                            </div>
                        </button>

                        {/* Product */}
                        <SubMenuList
                            label="product submenu"
                            menuText={productMenuText}
                            title={productTitle}
                            subMenus={productSubMenus}
                            isOpen={openSubMenu === 'product'}
                            onToggle={() => toggleSubMenu('product')}
                            onNavigate={navigate}
                            subMenuTitleColor={theme.subMenuTitleColor}
                            subMenuItemColor={theme.subMenuItemColor}
                            activeBg={theme.menuItemActiveBg}
                        />

                        {/* Blog */}
                        <SubMenuList
                            label="blog submenu"
                            menuText={blogMenuText}
                            title={blogTitle}
                            subMenus={blogSubMenus}
                            isOpen={openSubMenu === 'blog'}
                            onToggle={() => toggleSubMenu('blog')}
                            onNavigate={navigate}
                            subMenuTitleColor={theme.subMenuTitleColor}
                            subMenuItemColor={theme.subMenuItemColor}
                            activeBg={theme.menuItemActiveBg}
                        />

                        {/* Company */}
                        <SubMenuList
                            label="company submenu"
                            menuText={companyMenuText}
                            title={companyTitle}
                            subMenus={companySubMenus}
                            isOpen={openSubMenu === 'company'}
                            onToggle={() => toggleSubMenu('company')}
                            onNavigate={navigate}
                            subMenuTitleColor={theme.subMenuTitleColor}
                            subMenuItemColor={theme.subMenuItemColor}
                            activeBg={theme.menuItemActiveBg}
                        />

                        {/* Contact Us */}
                        <div className="j-h5 flex w-full flex-col items-center">
                            <button
                                onClick={openForm}
                                className={clsx(
                                    'mt-[24px] flex h-[48px] w-full items-center justify-center rounded-[64px] border',
                                    theme.contactBorder,
                                    theme.contactBg,
                                    theme.contactText,
                                )}
                            >
                                {contactUsMenuText}
                            </button>
                            {/* 语言切换 */}
                            <div className="mb-[24px] mt-[24px] flex items-center justify-center">
                                <Suspense
                                    fallback={
                                        <LoadingPlaceholder
                                            className={clsx(
                                                'h-[32px] w-[72px]',
                                                textColor === 'white'
                                                    ? 'bg-white/10'
                                                    : 'bg-gray-200',
                                            )}
                                        />
                                    }
                                >
                                    <LanguageChanger textColor={textColor} />
                                </Suspense>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
